// D-910: foreground-only feed/interest coordinator used by DiscoverMapScreen.
// No location permission, new share, point history or provider SDK is created here.
export type DiscoverMapShare=Readonly<{id:number;sharerId:number;name:string|null;
  photoUrl?:string|null;
  latitude:number|null;longitude:number|null;accuracyM:number|null;pointAt:string|null;expiresAt:string|null}>;
export type DiscoverMapState=Readonly<{status:'idle'|'loading'|'ready'|'error';shares:readonly DiscoverMapShare[]}>;
type State=DiscoverMapState;
type Timer=ReturnType<typeof setTimeout>|number;
type Dependencies={
  fetchPage:(viewerId:number,after:number,signal:AbortSignal)=>Promise<unknown>;
  publish:(state:State)=>void;
  interest?:(ids:number[],signal:AbortSignal)=>Promise<readonly number[]>;
  now?:()=>number;
  setTimer?:(fn:()=>void,ms:number)=>Timer;
  clearTimer?:(timer:Timer)=>void;
};
const id=(v:unknown):v is number=>Number.isSafeInteger(v)&&Number(v)>0;
const date=(v:unknown):v is string=>typeof v==='string'&&v.length<=40&&Number.isFinite(Date.parse(v));
function page(value:unknown,after:number) {
  if(!value||typeof value!=='object'||Array.isArray(value))throw Error('Invalid map page');
  const p=value as {shares:unknown;nextAfter:unknown};
  if(!Array.isArray(p.shares)||p.shares.length>100)throw Error('Invalid map page');
  let previous=after;
  const shares=p.shares.map((value):DiscoverMapShare=>{
    if(!value||typeof value!=='object'||Array.isArray(value))throw Error('Invalid map share');
    const s=value as Record<string,unknown>;
    if(!id(s.id)||s.id<=previous||!id(s.sharerId)||(s.name!==null&&(typeof s.name!=='string'||s.name.length>1000))
      ||(s.expiresAt!==null&&!date(s.expiresAt)))throw Error('Invalid map share');
    previous=s.id;
    const valid=typeof s.latitude==='number'&&Number.isFinite(s.latitude)&&Math.abs(s.latitude)<=90
      &&typeof s.longitude==='number'&&Number.isFinite(s.longitude)&&Math.abs(s.longitude)<=180&&date(s.pointAt);
    const photoUrl=typeof s.photoUrl==='string'&&s.photoUrl.length<=8192
      &&(/^(https?:\/\/|\/uploads\/)/.test(s.photoUrl))?s.photoUrl:null;
    return Object.freeze({id:s.id,sharerId:s.sharerId,name:s.name as string|null,photoUrl,
      latitude:valid?s.latitude as number:null,longitude:valid?s.longitude as number:null,
      accuracyM:valid&&id(s.accuracyM)?s.accuracyM:null,pointAt:valid?s.pointAt as string:null,
      expiresAt:s.expiresAt as string|null});
  });
  if(p.nextAfter!==null&&(!id(p.nextAfter)||p.shares.length!==100||p.nextAfter!==previous))throw Error('Invalid map cursor');
  return {shares,nextAfter:p.nextAfter as number|null};
}

export function createDiscoverMapSession(deps:Dependencies) {
  const now=deps.now??Date.now,setTimer=deps.setTimer??setTimeout,clearTimer=deps.clearTimer??clearTimeout;
  let viewer:number|null=null,active=false,epoch=0;
  let current:AbortController|null=null,poll:Timer|undefined,expiry:Timer|undefined,deadline:Timer|undefined;
  let state:State=Object.freeze({status:'idle',shares:Object.freeze([])});
  function clear(timer:Timer|undefined){if(timer!==undefined)clearTimer(timer);}
  function emit(status:State['status'],shares:readonly DiscoverMapShare[]) {
    clear(expiry);expiry=undefined;
    const live=shares.filter(s=>s.expiresAt===null||Date.parse(s.expiresAt)>now());
    state=Object.freeze({status,shares:Object.freeze(live)});
    if(active&&live.some(s=>s.expiresAt!==null)){
      const earliest=Math.min(...live.filter(s=>s.expiresAt!==null).map(s=>Date.parse(s.expiresAt!)));
      expiry=setTimer(()=>emit(state.status,state.shares),Math.min(2147483647,Math.max(1,earliest-now())));
    }
    deps.publish(state);
  }
  function stop() {
    epoch++;active=false;viewer=null;current?.abort();current=null;
    clear(poll);clear(expiry);clear(deadline);poll=expiry=deadline=undefined;emit('idle',[]);
  }
  async function refresh() {
    if(!active||viewer===null||current)return;
    clear(poll);poll=undefined;
    const ticket=epoch,actor=viewer,controller=new AbortController();current=controller;
    const valid=()=>active&&epoch===ticket&&current===controller&&!controller.signal.aborted;
    const schedule=()=>{if(active&&epoch===ticket)poll=setTimer(()=>{void refresh();},15000);};
    emit('loading',state.shares);
    // Includes every page and ignores late results even when an adapter does
    // not honor AbortSignal. A failed refresh never retains private points.
    deadline=setTimer(()=>{
      if(!valid())return;controller.abort();current=null;deadline=undefined;emit('error',[]);schedule();
    },10000);
    try {
      let after=0;const collected:DiscoverMapShare[]=[];
      for(let count=0;count<20;count++){
        const raw=await deps.fetchPage(actor,after,controller.signal);if(!valid())return;
        const next=page(raw,after);collected.push(...next.shares);
        if(next.nextAfter===null){
          // The map is actually visible, not just prefetched. The server checks
          // eligibility again; a withdrawn share cannot survive the interest readback.
          const accepted=new Set<number>();
          if(deps.interest)for(let start=0;start<collected.length;start+=100){
            if(!valid())return;
            const ids=collected.slice(start,start+100).filter(s=>s.expiresAt===null||Date.parse(s.expiresAt)>now()).map(s=>s.id);
            if(!ids.length)continue;
            const acknowledged=await deps.interest(ids,controller.signal);if(!valid())return;
            if(!Array.isArray(acknowledged)||acknowledged.some(n=>!ids.includes(n)))throw Error('Invalid map interest');
            acknowledged.forEach(n=>accepted.add(n));
          }
          emit('ready',deps.interest?collected.filter(s=>accepted.has(s.id)):collected);return;
        }
        after=next.nextAfter;
      }
      throw Error('Map page limit exceeded'); // Never publish a silently partial map.
    }catch{if(valid())emit('error',[]);}
    finally {
      if(current===controller){clear(deadline);deadline=undefined;current=null;schedule();}
    }
  }
  function setContext(viewerId:number|null,visible:boolean,foreground:boolean) {
    const enabled=id(viewerId)&&visible&&foreground;
    if(enabled&&active&&viewer===viewerId)return;
    stop();
    if(enabled){viewer=viewerId;active=true;void refresh();}
  }
  return {setContext,refresh,dispose:stop};
}
